import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Bell, Mail, MessageCircle, Loader2 } from 'lucide-react';

const Toggle = ({ checked, onChange, testid, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-[#7D202B]' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

export const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/notify/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ email_opt_in: true, whatsapp_opt_in: false, whatsapp_number: '' }));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(`${API}/notify/preferences`, patch, { headers: { Authorization: `Bearer ${token}` } });
      setPrefs(r.data);
      toast.success('Notification preferences updated');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="card p-6 flex justify-center"><Loader2 className="animate-spin text-[#7D202B]" /></div>;
  }

  return (
    <div className="card p-6" data-testid="notification-preferences">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={20} className="text-[#7D202B]" />
        <h3 className="text-lg font-heading font-semibold text-[#2D3748]">Notifications</h3>
      </div>
      <p className="text-xs text-[#718096] mb-4">
        We only send neutral reminders (e.g. “a session is confirmed”). We never send chat, therapy, or document content.
      </p>

      <div className="flex items-center justify-between py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-[#7A625C]" />
          <span className="text-sm text-[#2D3748]">Email updates</span>
        </div>
        <Toggle checked={!!prefs.email_opt_in} label="Email updates" testid="pref-email-toggle"
          onChange={(v) => save({ email_opt_in: v })} />
      </div>

      <div className="py-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#7A625C]" />
            <span className="text-sm text-[#2D3748]">WhatsApp updates (opt-in)</span>
          </div>
          <Toggle checked={!!prefs.whatsapp_opt_in} label="WhatsApp updates" testid="pref-whatsapp-toggle"
            onChange={(v) => {
              if (v && !prefs.whatsapp_number) {
                setPrefs({ ...prefs, whatsapp_opt_in: true });
                return; // require number entry first
              }
              save({ whatsapp_opt_in: v, whatsapp_number: prefs.whatsapp_number });
            }} />
        </div>
        {prefs.whatsapp_opt_in && (
          <div className="mt-3 flex gap-2">
            <input
              type="tel"
              value={prefs.whatsapp_number || ''}
              onChange={(e) => setPrefs({ ...prefs, whatsapp_number: e.target.value })}
              placeholder="+91 98765 43210"
              data-testid="pref-whatsapp-number"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
            <button
              onClick={() => save({ whatsapp_opt_in: true, whatsapp_number: prefs.whatsapp_number })}
              disabled={saving || !prefs.whatsapp_number}
              data-testid="pref-whatsapp-save"
              className="px-4 py-2 text-sm rounded-lg bg-[#7D202B] text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
        )}
        <p className="text-[11px] text-[#A0AEC0] mt-2">You can opt out any time. WhatsApp delivery is pending provider approval and is currently simulated.</p>
      </div>
    </div>
  );
};

export default NotificationPreferences;
